// SAD-857: <GlossaryText> underlines the program's glossary terms inside a
// string; a click opens the one shared glossary card. It renders the plain
// string until the glossary arrives (the page never waits on it), then the
// same characters with terms marked: no layout shift. The split is memoized
// per string, and the card's content renders only when it opens.
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraTerm } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { createContext, memo, useContext, useMemo } from 'react';
export const GlossaryContext = /*#__PURE__*/ createContext(null);
/** The program glossary, inside a program page; null outside one. */ export function useGlossary() {
    return useContext(GlossaryContext);
}
export const GlossaryText = /*#__PURE__*/ memo(function GlossaryText({ text }) {
    const ctx = useContext(GlossaryContext);
    const matcher = ctx?.matcher ?? null;
    const open = ctx?.open;
    const value = text ?? '';
    const segments = useMemo(()=>matcher ? matcher.split(value) : null, [
        matcher,
        value
    ]);
    if (!segments || !open || segments.length === 1 && !segments[0].key) return /*#__PURE__*/ _jsx(_Fragment, {
        children: value
    });
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: segments.map((s, i)=>s.key ? /*#__PURE__*/ _jsx(SpecraTerm, {
                "data-testid": Testids.glossary.term,
                "data-term": s.key,
                onClick: (e)=>{
                    // A term inside a clickable row opens its card, not the row.
                    e.stopPropagation();
                    e.preventDefault();
                    open(s.key, e.currentTarget);
                },
                onKeyDown: (e)=>{
                    if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
                },
                children: s.text
            }, i) : s.text)
    });
});
/**
 * How the program glossary prints a lowercase word, for event names (SAD-857);
 * undefined until the glossary arrives or outside a program.
 */ export function useGlossaryCasing() {
    return useContext(GlossaryContext)?.matcher?.printed;
}
